import React, { useEffect, useMemo, useState } from "react";
import { Button, Tag, Space, Typography, Tooltip, Input, Select, ConfigProvider, theme as antdTheme } from "antd";
import {
  PrinterOutlined,
  SaveOutlined,
  EditOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./ResultsTableModal.css";

const { Title, Text } = Typography;
const { Option } = Select;

// ✅ Détecte le data-theme sur <html> et reste réactif si le thème
// change dynamiquement (toggle utilisateur, media query, etc.)
function useIsDarkTheme() {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme") === "dark"
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const target = document.documentElement;
    const update = () => setIsDark(target.getAttribute("data-theme") === "dark");

    update();

    const observer = new MutationObserver(update);
    observer.observe(target, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

export default function ResultsTableModal({
  visible,
  result,
  onClose,
  onSave,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isDark = useIsDarkTheme();

  // ✅ FIX — extraction des données AVANT les hooks pour éviter la violation
  // des règles de React (hooks conditionnels)
  const parametres = useMemo(
    () => result?.data?.parametres || result?.parametres || [],
    [result]
  );
  const documentInfo = result?.data?.document_info || result?.document_info || {};
  const patientInfo = result?.data?.patient || result?.patient || {};
  const classification = result?.data?.classification || result?.classification || {};
  const laboratoire = result?.data?.laboratoire || result?.laboratoire || {};
  const extractionMetadata =
    result?.data?.extraction_metadata || result?.extraction_metadata || {};
  const analysisIncomplete =
    extractionMetadata.ocr_success === false ||
    extractionMetadata.extraction_success === false;
  const unknownParameterLabel = t("resultsTable.unknown");

  // ✅ FIX — tous les useState AVANT le return conditionnel
  const [originalParameters, setOriginalParameters] = useState(
    Array.isArray(parametres)
      ? parametres.map((param) => ({
          nom: param.nom || t("resultsTable.unknown"),
          valeur: param.valeur || "-",
          unite: param.unite || "-",
          normale: param.normale || "-",
          statut: param.statut || "NORMAL",
        }))
      : []
  );

  const [editableParameters, setEditableParameters] = useState(
    [...originalParameters]
  );
  const [modifications, setModifications] = useState({});

  useEffect(() => {
    if (!visible) return;

    const normalized = Array.isArray(parametres)
      ? parametres.map((param) => ({
          nom: param.nom || unknownParameterLabel,
          valeur: param.valeur ?? "-",
          unite: param.unite ?? "-",
          normale: param.normale ?? "-",
          statut: param.statut || "NORMAL",
        }))
      : [];

    setOriginalParameters(normalized);
    setEditableParameters(normalized.map((param) => ({ ...param })));
    setModifications({});
  }, [visible, parametres, unknownParameterLabel]);

  // ✅ Return conditionnel APRÈS tous les hooks
  if (!visible) return null;

  const handleChange = (index, field, newValue) => {
    const oldValue = originalParameters[index]?.[field];

    const updated = [...editableParameters];
    updated[index][field] = newValue;
    setEditableParameters(updated);

    const modKey = `${index}-${field}`;

    if (newValue !== oldValue) {
      setModifications((prev) => ({
        ...prev,
        [modKey]: {
          field,
          index,
          parameter: originalParameters[index]?.nom,
          oldValue,
          newValue,
          timestamp: new Date().toISOString(),
        },
      }));
    } else {
      setModifications((prev) => {
        const next = { ...prev };
        delete next[modKey];
        return next;
      });
    }
  };

  const isModified = (index, field) => {
    const modKey = `${index}-${field}`;
    return modifications[modKey] !== undefined;
  };

  const getInterpretation = (status) => {
    switch (status?.toUpperCase()) {
      case "NORMAL":
        return {
          text: t("resultsTable.interpretation.normal.text"),
          detail: t("resultsTable.interpretation.normal.detail"),
          icon: "✓",
        };
      case "LOW":
      case "BAS":
        return {
          text: t("resultsTable.interpretation.low.text"),
          detail: t("resultsTable.interpretation.low.detail"),
          icon: "↓",
        };
      case "HIGH":
      case "ELEVE":
      case "ÉLEVÉ":
        return {
          text: t("resultsTable.interpretation.high.text"),
          detail: t("resultsTable.interpretation.high.detail"),
          icon: "↑",
        };
      case "CRITIQUE":
        return {
          text: t("resultsTable.interpretation.critical.text"),
          detail: t("resultsTable.interpretation.critical.detail"),
          icon: "⚠",
        };
      default:
        return {
          text: t("resultsTable.interpretation.unknown.text"),
          detail: t("resultsTable.interpretation.unknown.detail"),
          icon: "?",
        };
    }
  };

  const columns = [
    {
      title: t("resultsTable.columns.parameter"),
      dataIndex: "nom",
      key: "nom",
      width: "18%",
      render: (text, record, index) => (
        <Input
          value={text}
          disabled={readOnly}
          onChange={(e) => handleChange(index, "nom", e.target.value)}
          className={`editable-cell-input ${
            isModified(index, "nom") ? "modified" : ""
          }`}
          placeholder={t("resultsTable.placeholders.parameter")}
        />
      ),
    },
    {
      title: t("resultsTable.columns.value"),
      dataIndex: "valeur",
      key: "valeur",
      width: "12%",
      align: "center",
      render: (value, record, index) => (
        <Input
          value={value}
          disabled={readOnly}
          onChange={(e) => handleChange(index, "valeur", e.target.value)}
          className={`editable-cell-input ${
            isModified(index, "valeur") ? "modified" : ""
          }`}
          placeholder={t("resultsTable.placeholders.value")}
          style={{ textAlign: "center" }}
        />
      ),
    },
    {
      title: t("resultsTable.columns.unit"),
      dataIndex: "unite",
      key: "unite",
      width: "10%",
      align: "center",
      render: (unit, record, index) => (
        <Input
          value={unit}
          disabled={readOnly}
          onChange={(e) => handleChange(index, "unite", e.target.value)}
          className={`editable-cell-input ${
            isModified(index, "unite") ? "modified" : ""
          }`}
          placeholder={t("resultsTable.placeholders.unit")}
          style={{ textAlign: "center" }}
        />
      ),
    },
    {
      title: t("resultsTable.columns.normalRange"),
      dataIndex: "normale",
      key: "normale",
      width: "18%",
      align: "center",
      render: (range, record, index) => (
        <Input
          value={range}
          disabled={readOnly}
          onChange={(e) => handleChange(index, "normale", e.target.value)}
          className={`editable-cell-input ${
            isModified(index, "normale") ? "modified" : ""
          }`}
          placeholder={t("resultsTable.placeholders.normalRange")}
          style={{ textAlign: "center" }}
        />
      ),
    },
    {
      title: t("resultsTable.columns.status"),
      dataIndex: "statut",
      key: "statut",
      width: "15%",
      align: "center",
      render: (status, record, index) => (
        <Select
          value={status}
          disabled={readOnly}
          onChange={(value) => handleChange(index, "statut", value)}
          className={`editable-cell-select ${
            isModified(index, "statut") ? "modified" : ""
          }`}
          style={{ width: "100%" }}
        >
          <Option value="NORMAL">
            <Tag color="success">✓ {t("resultsTable.status.normal")}</Tag>
          </Option>
          <Option value="LOW">
            <Tag color="warning">↓ {t("resultsTable.status.low")}</Tag>
          </Option>
          <Option value="HIGH">
            <Tag color="error">↑ {t("resultsTable.status.high")}</Tag>
          </Option>
          <Option value="CRITIQUE">
            <Tag color="error">⚠ {t("resultsTable.status.critical")}</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: t("resultsTable.columns.interpretation"),
      key: "interpretation",
      width: "20%",
      render: (_, record) => {
        const interpretation = getInterpretation(record.statut);
        return (
          <Tooltip title={interpretation.detail}>
            <div className="interpretation-cell">
              <span className="interpretation-icon">{interpretation.icon}</span>
              <span>{interpretation.text}</span>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const handleApprove = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        parameters: editableParameters,
        modifications: Object.values(modifications),
        metadata: {
          totalChanges: Object.keys(modifications).length,
          savedAt: new Date().toISOString(),
        },
      };

      if (onSave) {
        await onSave(dataToSave);
      } else {
        throw new Error("No onSave handler provided");
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const modificationsCount = Object.keys(modifications).length;

  // ✅ Thème Ant Design synchronisé avec data-theme, pour que Select,
  // Tag, Tooltip (montés en portal) suivent le dark mode automatiquement.
  const antdThemeConfig = {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: "#006eea",
    },
  };

  // Cas : aucun paramètre à afficher
  if (editableParameters.length === 0) {
    return (
      <ConfigProvider theme={antdThemeConfig}>
        <div
          className={`results-modal-overlay ${visible ? "visible" : ""}`}
          onClick={onClose}
        >
          <div
            className="results-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="results-close-button" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="results-modal-content">
              <Title level={3}>
                {analysisIncomplete
                  ? t("upload.analysisIncompleteTitle")
                  : t("resultsTable.noParametersTitle")}
              </Title>
              <Text type="secondary">
                {analysisIncomplete
                  ? t("upload.analysisIncompleteMessage")
                  : t("resultsTable.noParametersMessage")}
              </Text>
              <div style={{ marginTop: 24 }}>
                <Button type="primary" onClick={onClose}>
                  {t("resultsTable.close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antdThemeConfig}>
      <div
        className={`results-modal-overlay ${visible ? "visible" : ""}`}
        onClick={onClose}
      >
        <div
          className="results-modal-container"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="results-close-button" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div className="results-modal-content">
            <div style={{ marginBottom: 24 }}>
              <Title
                level={3}
                style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
              >
                <EditOutlined className="results-title-icon" />
                {t("resultsTable.title")}
                {result?.documentType && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {result.documentType}
                  </Tag>
                )}
                {modificationsCount > 0 && (
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    <HistoryOutlined />{" "}
                    {t("resultsTable.changes", { count: modificationsCount })}
                  </Tag>
                )}
              </Title>

              <Space size="middle" style={{ marginTop: 8 }}>
                {patientInfo?.nom && (
                  <Text type="secondary">
                    {t("resultsTable.patient")}: {patientInfo.nom}
                  </Text>
                )}
                {documentInfo?.date_analyse && (
                  <Text type="secondary">
                    {t("resultsTable.date")}: {documentInfo.date_analyse}
                  </Text>
                )}
                {classification?.confiance && (
                  <Text type="secondary">
                    {t("resultsTable.confidence")}: {classification.confiance}%
                  </Text>
                )}
                {laboratoire?.nom && (
                  <Text type="secondary">
                    {t("resultsTable.lab")}: {laboratoire.nom}
                  </Text>
                )}
              </Space>
            </div>

            <div className="results-table-wrapper">
              <table className="results-table editable-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        style={{ width: col.width, textAlign: col.align }}
                      >
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {editableParameters.map((param, index) => (
                    <tr key={index}>
                      {columns.map((col) => (
                        <td key={col.key} style={{ textAlign: col.align }}>
                          {col.render
                            ? col.render(param[col.dataIndex], param, index)
                            : param[col.dataIndex]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editableParameters.length > 0 && (
              <div className="results-stats-bar">
                <Text>
                  <strong>{t("resultsTable.stats.total")}:</strong>{" "}
                  {editableParameters.length}
                </Text>
                <Text>
                  <strong>{t("resultsTable.stats.normal")}:</strong>{" "}
                  <Tag color="success">
                    {editableParameters.filter((p) => p.statut === "NORMAL").length}
                  </Tag>
                </Text>
                <Text>
                  <strong>{t("resultsTable.stats.abnormal")}:</strong>{" "}
                  <Tag color="error">
                    {
                      editableParameters.filter(
                        (p) => p.statut !== "NORMAL" && p.statut !== "INCONNU"
                      ).length
                    }
                  </Tag>
                </Text>
              </div>
            )}

            <div className="results-actions">
              <button
                className="results-btn results-btn-secondary"
                onClick={handlePrint}
              >
                <PrinterOutlined />
                {t("resultsTable.printRecord")}
              </button>
              {readOnly ? (
                <button
                  className="results-btn results-btn-primary"
                  onClick={onClose}
                >
                  {t("resultsTable.close")}
                </button>
              ) : (
                <button
                  className="results-btn results-btn-primary"
                  onClick={handleApprove}
                  disabled={saving}
                >
                  <SaveOutlined />
                  {saving
                    ? t("resultsTable.saving")
                    : modificationsCount > 0
                    ? t("resultsTable.saveChanges", { count: modificationsCount })
                    : t("resultsTable.saveChangesDefault")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}